import React from 'react';
import { AlertTriangle } from 'lucide-react';

export interface ConversationUsageProgressBarProps {
  /** Conversations included in the plan (the 100% mark) */
  limit: number;
  /** Conversations used in the current cycle */
  used: number;
  /** What the conversations beyond the plan cost — only ever non-zero on a metered plan */
  overage?: number;
  /** Warning threshold marker (default 80) */
  warningThresholdPct?: number;
  /** Currency symbol for the overage amount (default €) */
  currencySymbol?: string;
}

/**
 * How much of this month's conversations are gone.
 *
 * Counted in CONVERSATIONS, which is the only unit the plan is sold in. It used to render the same
 * two numbers with a euro sign in front of them — so a merchant on Growth read "€556.00 / €2500.00"
 * when what they had was 556 of 2,500 conversations. The currency belongs to exactly one figure
 * here: the overage, which is money, and which a capped plan can never produce.
 */
const ConversationUsageProgressBar: React.FC<ConversationUsageProgressBarProps> = ({
  limit,
  used,
  overage = 0,
  warningThresholdPct = 80,
  currencySymbol = '€',
}) => {
  const safeLimit = limit > 0 ? limit : 0;
  const rawPct = safeLimit > 0 ? (used / safeLimit) * 100 : 0;
  const isOver = rawPct >= 100 || overage > 0;
  const isWarning = rawPct >= warningThresholdPct && !isOver;

  // Visual fill is capped at 100%; overage is communicated via the label.
  const fillPct = Math.min(rawPct, 100);

  const barColor = isOver
    ? 'bg-red-500'
    : isWarning
      ? 'bg-yellow-500'
      : 'bg-primary';

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-medium dark:text-text-secondary text-light-text-secondary">
            Conversations
          </span>
          <span className="text-base font-semibold dark:text-text-primary text-light-text-primary">
            {Math.round(used).toLocaleString()}
            <span className="text-xs font-normal dark:text-text-secondary text-light-text-secondary">
              {' '}/ {Math.round(safeLimit).toLocaleString()} included
            </span>
          </span>
        </div>
        <span
          className={`text-sm font-semibold ${
            isOver
              ? 'text-red-500'
              : isWarning
                ? 'text-yellow-500'
                : 'text-primary'
          }`}
        >
          {rawPct.toFixed(0)}%
        </span>
      </div>

      <div className="relative w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-500 rounded-full`}
          style={{ width: `${fillPct}%` }}
          role="progressbar"
          aria-valuenow={Math.round(rawPct)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
      </div>

      {/* Threshold markers */}
      <div className="relative w-full h-3 -mt-3 pointer-events-none">
        <div
          className="absolute top-0 h-3 w-px bg-gray-400 dark:bg-gray-500"
          style={{ left: `${warningThresholdPct}%` }}
          aria-hidden
        />
        <div
          className="absolute top-0 h-3 w-px bg-gray-400 dark:bg-gray-500"
          style={{ left: '100%', transform: 'translateX(-1px)' }}
          aria-hidden
        />
      </div>

      <div className="relative w-full mt-1 text-[10px] dark:text-text-secondary text-light-text-secondary">
        <span
          className="absolute"
          style={{ left: `${warningThresholdPct}%`, transform: 'translateX(-50%)' }}
        >
          {warningThresholdPct}%
        </span>
        <span className="absolute right-0">100%</span>
        <span className="invisible">.</span>
      </div>

      {isOver && (
        <div className="mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-md bg-red-500/10 text-red-600 dark:text-red-300 border border-red-500/30">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            {Math.round(used - safeLimit).toLocaleString()} conversations beyond the plan
            {overage > 0 && (
              <>
                {' — '}
                <span className="font-semibold">
                  {currencySymbol}
                  {overage.toFixed(2)}
                </span>{' '}
                this cycle
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default ConversationUsageProgressBar;
